import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  MoveUp,
  MoveDown,
  ChevronDown,
  ChevronUp,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { usePost } from "@/hooks/blog/usePost";
import { useUpdatePost } from "@/hooks/blog/useEditPost";
import { useCategories } from "@/hooks/blog/useCategories";
import { getImageUrl } from "@/api/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RichTextEditor from "@/components/ui/rich-text-editor";
import LivePreview from "@/components/ui/live-preview";
import { ImageUploader } from "@/components/ui/image-uploader";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const createSectionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export default function BlogEdit() {
  const { id } = useParams();

  const [isDirty, setIsDirty] = useState(false);
  const [initialData, setInitialData] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    category_id: "",
    featured_image: "",
    is_published: false,
    is_featured: false,
    tags: "",
  });

  const [pendingFeaturedImage, setPendingFeaturedImage] = useState(null);
  const [featuredImageRemoved, setFeaturedImageRemoved] = useState(false);

  const [sections, setSections] = useState([
    {
      id: createSectionId(),
      title: "",
      content: "",
      image: "",
      imagePreview: null,
      pendingFile: null,
      isOpen: true,
    },
  ]);

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: postData, isLoading: isLoadingPost, error: fetchError } = usePost(id);
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  const updatePostMutation = useUpdatePost();

  const categories = categoriesData?.data || [];
  const isLoading = isLoadingPost || isLoadingCategories;
  const isSubmitting = updatePostMutation.isPending;

  // Component to conditionally show tooltip only when text is truncated
  const TextWithTooltip = ({ text, lineClamp = "truncate", className = "" }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (textRef.current) {
          // Check if text is truncated by comparing scrollWidth/scrollHeight with clientWidth/clientHeight
          const isClamped =
            textRef.current.scrollHeight > textRef.current.clientHeight ||
            textRef.current.scrollWidth > textRef.current.clientWidth;
          setIsTruncated(isClamped);
        }
      };

      checkTruncation();
      // Recheck on window resize
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }, [text]);

    const textElement = (
      <div
        ref={textRef}
        className={`${lineClamp} ${className}`}
        style={{ cursor: isTruncated ? "help" : "default" }}>
        {text}
      </div>
    );

    if (!isTruncated) {
      return textElement;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  // Component to conditionally show tooltip only when content is truncated
  const ContentWithTooltip = ({ content }) => {
    const contentRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const checkTruncation = () => {
        if (contentRef.current) {
          // Check if content is truncated by comparing scrollHeight with clientHeight
          const isClamped = contentRef.current.scrollHeight > contentRef.current.clientHeight;
          setIsTruncated(isClamped);
        }
      };

      checkTruncation();
      // Recheck on window resize
      window.addEventListener("resize", checkTruncation);
      return () => window.removeEventListener("resize", checkTruncation);
    }, [content]);

    const contentElement = (
      <div
        ref={contentRef}
        className="line-clamp-4 overflow-hidden"
        style={{ cursor: isTruncated ? "help" : "default" }}>
        <div
          className="prose prose-sm max-w-none text-gray-700 *:my-0 *:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    );

    if (!isTruncated) {
      return contentElement;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{contentElement}</TooltipTrigger>
        <TooltipContent side="top" className="max-h-96 max-w-md overflow-y-auto">
          <div
            className="prose prose-sm max-w-none text-xs"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </TooltipContent>
      </Tooltip>
    );
  };

  const normalizeImagePath = (src) => {
    if (!src) return "";
    if (src.startsWith("http://") || src.startsWith("https://")) {
      try {
        const parsedUrl = new URL(src);
        return parsedUrl.pathname;
      } catch {
        return src;
      }
    }
    return src;
  };

  const resolvePreviewUrl = (src) => {
    if (!src) return "";
    return src.startsWith("http://") || src.startsWith("https://") ? src : getImageUrl(src);
  };

  const parseContentToSections = (htmlContent) => {
    if (!htmlContent)
      return [
        {
          id: createSectionId(),
          title: "",
          content: "",
          image: "",
          imagePreview: null,
          isOpen: true,
        },
      ];

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    const wrappedSections = Array.from(doc.querySelectorAll('section[data-blog-section="true"]'));
    if (wrappedSections.length > 0) {
      const parsedWrappedSections = wrappedSections.map((sectionEl, index) => {
        let title = "";
        let image = "";
        let imagePreview = null;
        const contentElements = [];

        Array.from(sectionEl.children).forEach((child) => {
          if (child.tagName === "H2" && !title) {
            title = child.textContent || "";
            return;
          }

          if (child.tagName === "IMG" && !image) {
            const rawSrc = child.getAttribute("src") || "";
            const normalizedSrc = normalizeImagePath(rawSrc);
            image = normalizedSrc;
            imagePreview = resolvePreviewUrl(normalizedSrc);
            return;
          }

          contentElements.push(child.outerHTML);
        });

        return {
          id: createSectionId(),
          title,
          content: contentElements.join("\n"),
          image,
          imagePreview,
          isOpen: index === 0,
        };
      });

      const validWrappedSections = parsedWrappedSections.filter(
        (section) => section.title || section.content || section.image
      );

      if (validWrappedSections.length > 0) {
        validWrappedSections[0].isOpen = true;
        return validWrappedSections;
      }
    }

    const parsedSections = [];

    let currentSection = {
      id: createSectionId(),
      title: "",
      content: "",
      image: "",
      imagePreview: null,
      isOpen: false,
    };

    const contentElements = [];

    Array.from(doc.body.children).forEach((element, index) => {
      if (element.tagName === "H2") {
        if (contentElements.length > 0) {
          currentSection.content = contentElements.map((el) => el.outerHTML).join("\n");
          contentElements.length = 0;
        }
        if (currentSection.title || currentSection.content || currentSection.image) {
          parsedSections.push(currentSection);
        }
        currentSection = {
          id: createSectionId(),
          title: element.textContent,
          content: "",
          image: "",
          imagePreview: null,
          isOpen: false,
        };
      } else if (element.tagName === "IMG") {
        const rawSrc = element.getAttribute("src") || "";
        const normalizedSrc = normalizeImagePath(rawSrc);
        currentSection.image = normalizedSrc;
        currentSection.imagePreview = resolvePreviewUrl(normalizedSrc);
      } else {
        contentElements.push(element);
      }
    });

    if (contentElements.length > 0) {
      currentSection.content = contentElements.map((el) => el.outerHTML).join("\n");
    }

    if (currentSection.title || currentSection.content || currentSection.image) {
      parsedSections.push(currentSection);
    }

    if (parsedSections.length > 0) parsedSections[0].isOpen = true;

    return parsedSections.length > 0
      ? parsedSections
      : [
          {
            id: createSectionId(),
            title: "",
            content: "",
            image: "",
            imagePreview: null,
            isOpen: true,
          },
        ];
  };

  useEffect(() => {
    if (postData?.data && categoriesData?.data && !isInitialized) {
      const post = postData.data;
      const tagsString = post.tags || "";

      const initialFormState = {
        title: post.title || "",
        excerpt: post.excerpt || "",
        category_id: post.category_id?.toString() || "",
        featured_image: post.featured_image || "",
        is_published: Boolean(post.is_published),
        is_featured: Boolean(post.is_featured),
        tags: tagsString,
      };

      const parsedSections = parseContentToSections(post.content);

      setFormData(initialFormState);
      setSections(parsedSections);

      setInitialData({
        formData: initialFormState,
        sections: JSON.parse(JSON.stringify(parsedSections)),
      });

      if (post.featured_image) {
        setImagePreview(getImageUrl(post.featured_image));
      }
      setIsInitialized(true);
    }
  }, [postData, categoriesData, isInitialized]);

  useEffect(() => {
    if (!initialData || !isInitialized) {
      setIsDirty(false);
      return;
    }

    const formChanged = Object.keys(initialData.formData).some(
      (key) => formData[key] !== initialData.formData[key]
    );

    if (
      formChanged ||
      pendingFeaturedImage ||
      featuredImageRemoved ||
      sections.length !== initialData.sections.length
    ) {
      setIsDirty(true);
      return;
    }

    const sectionsChanged = sections.some((section, index) => {
      const initialSection = initialData.sections[index];
      if (!initialSection) return true;
      if (section.image instanceof File) return true;

      return (
        section.title !== initialSection.title ||
        section.content !== initialSection.content ||
        section.image !== initialSection.image
      );
    });

    setIsDirty(sectionsChanged);
  }, [formData, sections, pendingFeaturedImage, featuredImageRemoved, initialData, isInitialized]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageSelect = (file, previewUrl) => {
    setPendingFeaturedImage(file);
    setImagePreview(previewUrl);
    setFeaturedImageRemoved(false);
    setFormData((prev) => ({ ...prev, featured_image: "" }));
    setErrors((prev) => ({ ...prev, featured_image: "" }));
  };

  const handleRemoveImage = () => {
    setPendingFeaturedImage(null);
    setFormData((prev) => ({ ...prev, featured_image: "" }));
    setImagePreview(null);
    setFeaturedImageRemoved(true);
  };

  const addSection = () => {
    setSections((prev) => [
      ...prev.map((s) => ({ ...s, isOpen: false })),
      {
        id: createSectionId(),
        title: "",
        content: "",
        image: "",
        imagePreview: null,
        pendingFile: null,
        isOpen: true,
      },
    ]);
  };

  const toggleSection = (id) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, isOpen: !s.isOpen } : s)));
  };

  const removeSection = (id) => {
    if (sections.length === 1) {
      toast.error("You must have at least one section");
      return;
    }
    setSections((prev) => prev.filter((section) => section.id !== id));
  };

  const updateSection = (id, field, value) => {
    setSections((prev) =>
      prev.map((section) => (section.id === id ? { ...section, [field]: value } : section))
    );
  };

  const moveSection = (index, direction) => {
    setSections((prev) => {
      if (
        (direction === "up" && index === 0) ||
        (direction === "down" && index === prev.length - 1)
      ) {
        return prev;
      }

      const newSections = [...prev];
      const swapIndex = direction === "up" ? index - 1 : index + 1;
      [newSections[swapIndex], newSections[index]] = [newSections[index], newSections[swapIndex]];
      return newSections;
    });
  };

  const handleSectionImageSelect = (sectionId, file, previewUrl) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, image: file, imagePreview: previewUrl } : section
      )
    );
  };

  const handleRemoveSectionImage = (sectionId) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, image: null, imagePreview: null } : section
      )
    );
  };

  const escapeHtml = (text) => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  const generateContentFromSections = () => {
    let newImageIndex = 0;
    return sections
      .map((section) => {
        let html = "";
        if (section.title.trim()) html += `<h2>${escapeHtml(section.title)}</h2>\n`;
        if (section.image) {
          if (section.image instanceof File) {
            html += `<img src="__SECTION_IMAGE_${newImageIndex}__" alt="${escapeHtml(section.title || "Section image")}" />\n`;
            newImageIndex++;
          } else {
            html += `<img src="${section.image}" alt="${escapeHtml(section.title || "Section image")}" />\n`;
          }
        }
        if (section.content.trim()) {
          html += section.content + "\n";
        }

        const sectionHtml = html.trim();
        if (!sectionHtml) return "";

        return `<section data-blog-section="true">\n${sectionHtml}\n</section>`;
      })
      .filter(Boolean)
      .join("\n");
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.excerpt.trim()) newErrors.excerpt = "Excerpt is required";
    if (!formData.category_id) newErrors.category_id = "Category is required";
    const hasContent = sections.some((section) => section.title.trim() || section.content.trim());
    if (!hasContent) newErrors.sections = "At least one section must have content";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors before saving.");
      return;
    }

    const generatedContent = generateContentFromSections();
    const tags = formData.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    const sectionImages = sections
      .filter((section) => section.image instanceof File)
      .map((section) => section.image);

    let featuredImageValue;
    if (pendingFeaturedImage) featuredImageValue = pendingFeaturedImage;
    else if (featuredImageRemoved) featuredImageValue = null;
    else featuredImageValue = formData.featured_image || null;

    const postDataToSubmit = {
      title: formData.title,
      excerpt: formData.excerpt,
      content: generatedContent,
      category_id: parseInt(formData.category_id),
      featured_image: featuredImageValue,
      is_published: formData.is_published,
      is_featured: formData.is_featured,
      tags: tags.length > 0 ? tags : undefined,
      section_images: sectionImages.length > 0 ? sectionImages : undefined,
    };

    updatePostMutation.mutate({ id, postData: postDataToSubmit });
  };

  if (isLoading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto mb-4 h-10 w-10 animate-spin sm:h-12 sm:w-12" />
          <p className="text-muted-foreground text-base sm:text-lg">Loading post content...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="mx-auto mt-10 max-w-2xl rounded-xl border border-red-200 bg-red-50 p-6 text-center sm:mt-20 sm:p-10">
        <p className="mb-6 text-lg font-medium text-red-600 sm:text-xl">Failed to load post</p>
        <Link to="/dashboard/blogs">
          <Button size="lg" className="w-full sm:w-auto">
            Back to Posts
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-450 min-w-0 space-y-6 p-4 sm:space-y-8 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <Link to="/dashboard/blogs" className="w-fit">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-10 w-10">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Back to posts</TooltipContent>
          </Tooltip>
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
            Edit Post
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Update content for "{formData.title}"
          </p>
        </div>
      </div>

      {/* Applied max-w-full to absolutely prevent stretch */}
      <form onSubmit={handleSubmit} className="w-full max-w-full min-w-0">
        <div className="grid w-full max-w-full min-w-0 items-start gap-6 lg:grid-cols-[1fr_350px] lg:gap-8 xl:grid-cols-[1fr_500px] xl:gap-12">
          {/* LEFT COLUMN - FORM */}
          <div className="flex w-full max-w-full min-w-0 flex-col space-y-6 sm:space-y-8">
            <Card className="w-full max-w-full min-w-0 overflow-hidden shadow-sm">
              <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-4">
                <CardTitle className="text-lg sm:text-xl">Post Details</CardTitle>
              </CardHeader>
              <CardContent className="w-full max-w-full min-w-0 space-y-4 p-4 pt-0 sm:space-y-6 sm:p-6">
                <div className="w-full min-w-0 space-y-2">
                  <Label htmlFor="title" className="text-sm sm:text-base">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter an engaging title"
                    className={`h-11 w-full min-w-0 text-base font-medium sm:h-12 sm:text-lg ${errors.title ? "border-red-500" : ""}`}
                  />
                  {errors.title && (
                    <p className="text-xs text-red-500 sm:text-sm">{errors.title}</p>
                  )}
                </div>

                <div className="w-full min-w-0 space-y-2">
                  <Label htmlFor="excerpt" className="text-sm sm:text-base">
                    Excerpt <span className="text-red-500">*</span>
                  </Label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Brief summary appearing on the blog card..."
                    rows={4}
                    className={`flex w-full min-w-0 rounded-md border ${
                      errors.excerpt ? "border-red-500" : "border-input"
                    } bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring px-3 py-3 text-base focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-3`}
                  />
                  {errors.excerpt && (
                    <p className="text-xs text-red-500 sm:text-sm">{errors.excerpt}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sections Area */}
            <div className="w-full max-w-full min-w-0 space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="flex items-center gap-2 text-lg font-semibold sm:text-xl">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6" /> Content Sections
                </h3>
                <Button
                  type="button"
                  onClick={addSection}
                  size="default"
                  className="w-full sm:h-11 sm:w-auto sm:px-8 cursor-pointer">
                  <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" /> Add Section
                </Button>
              </div>

              {errors.sections && (
                <p className="rounded bg-red-50 p-2 text-xs text-red-500 sm:text-sm">
                  {errors.sections}
                </p>
              )}

              <div className="w-full max-w-full min-w-0 space-y-4 sm:space-y-6">
                {sections.map((section, index) => (
                  <Card
                    key={section.id}
                    className={`w-full max-w-full min-w-0 overflow-hidden shadow-sm transition-all duration-200 ${section.isOpen ? "ring-primary/20 ring-1" : "opacity-90 hover:opacity-100"}`}>
                    <div className="bg-muted/20 flex w-full min-w-0 items-center justify-between gap-2 border-b p-3 sm:p-5">
                      <div
                        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 sm:gap-3"
                        onClick={() => toggleSection(section.id)}>
                        {section.isOpen ? (
                          <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                        ) : (
                          <ChevronUp className="text-muted-foreground h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                        )}
                        <span className="text-sm font-semibold whitespace-nowrap sm:text-base">
                          Sec {index + 1}
                        </span>
                        {section.title && (
                          <span className="text-muted-foreground ml-1 truncate border-l pl-2 text-xs sm:ml-2 sm:pl-3 sm:text-sm">
                            {section.title}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={() => moveSection(index, "up")}
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 sm:h-9 sm:w-9"
                              disabled={index === 0}>
                              <MoveUp className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Move section up</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={() => moveSection(index, "down")}
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 sm:h-9 sm:w-9"
                              disabled={index === sections.length - 1}>
                              <MoveDown className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Move section down</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              onClick={() => removeSection(section.id)}
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600 sm:h-9 sm:w-9 cursor-pointer">
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Delete section</TooltipContent>
                        </Tooltip>
                      </div>
                    </div>

                    {section.isOpen && (
                      <CardContent className="w-full max-w-full min-w-0 space-y-6 p-4 pt-6 sm:space-y-8 sm:p-6 sm:pt-8">
                        <div className="w-full max-w-full min-w-0 space-y-4 sm:space-y-6">
                          <div className="w-full max-w-full min-w-0 space-y-2 sm:space-y-3">
                            <Label className="text-sm sm:text-base">Section Title</Label>
                            <Input
                              value={section.title}
                              onChange={(e) => updateSection(section.id, "title", e.target.value)}
                              placeholder="Section heading..."
                              className="h-11 w-full min-w-0 text-base sm:h-12"
                            />
                          </div>
                          <div className="w-full max-w-full min-w-0 space-y-2 sm:space-y-3">
                            <Label className="text-sm sm:text-base">Image</Label>
                            {/* 🛠️ FIX 1: Strict containment rules for Section images to prevent layout breaks */}
                            <div className="w-full max-w-full min-w-0 overflow-hidden [&_img]:h-auto [&_img]:max-w-full [&_img]:object-contain">
                              <ImageUploader
                                id={`sec-img-${section.id}`}
                                preview={section.imagePreview}
                                onImageSelect={(file, previewUrl) =>
                                  handleSectionImageSelect(section.id, file, previewUrl)
                                }
                                onImageRemove={() => handleRemoveSectionImage(section.id)}
                                disabled={isSubmitting}
                              />
                            </div>
                          </div>
                          <div className="w-full max-w-full min-w-0 space-y-2 sm:space-y-3">
                            <Label className="text-sm sm:text-base">Content</Label>
                            <div className="w-full min-w-0 overflow-x-auto pb-2 sm:overflow-visible">
                              <RichTextEditor
                                key={`editor-${section.id}`}
                                value={section.content}
                                onChange={(content) =>
                                  updateSection(section.id, "content", content)
                                }
                                placeholder="Write your content here..."
                                height={350}
                              />
                            </div>
                            <p className="text-muted-foreground text-right text-xs sm:text-sm">
                              Use the toolbar to format your content.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Bottom Organization & Metadata */}
            <div className="grid w-full max-w-full min-w-0 gap-6 sm:gap-8 lg:grid-cols-2">
              <Card className="w-full max-w-full min-w-0 overflow-hidden shadow-sm">
                <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Organization</CardTitle>
                </CardHeader>
                <CardContent className="w-full max-w-full min-w-0 space-y-4 p-4 pt-0 sm:space-y-5 sm:p-6">
                  <div className="w-full min-w-0 space-y-2 sm:space-y-3">
                    <Label htmlFor="category_id" className="text-sm sm:text-base">
                      Category <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`category-select-${formData.category_id}`}
                      value={formData.category_id}
                      onValueChange={(val) => {
                        setFormData((p) => ({ ...p, category_id: val }));
                        setErrors((p) => ({ ...p, category_id: "" }));
                      }}>
                      <SelectTrigger
                        className={`h-11 w-full min-w-0 text-base ${errors.category_id ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id.toString()}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category_id && (
                      <p className="text-xs text-red-500 sm:text-sm">{errors.category_id}</p>
                    )}
                  </div>

                  <div className="w-full min-w-0 space-y-2 sm:space-y-3">
                    <Label htmlFor="tags" className="text-sm sm:text-base">
                      Tags
                    </Label>
                    <Input
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="health, news, update"
                      className="h-11 w-full min-w-0 text-base sm:h-12"
                    />
                    <p className="text-muted-foreground text-xs sm:text-sm">Comma separated</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="w-full max-w-full min-w-0 overflow-hidden shadow-sm">
                <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Featured Image</CardTitle>
                </CardHeader>
                <CardContent className="w-full max-w-full min-w-0 p-4 pt-0 sm:p-6">
                  {/* 🛠️ FIX 2: Added utility classes to ensure the massive image shrinks correctly */}
                  <div className="w-full max-w-full min-w-0 overflow-hidden [&_img]:h-auto [&_img]:max-w-full [&_img]:object-contain">
                    <ImageUploader
                      id="featured_image"
                      preview={imagePreview}
                      onImageSelect={handleImageSelect}
                      onImageRemove={handleRemoveImage}
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.featured_image && (
                    <p className="mt-2 text-xs text-red-500 sm:text-sm">{errors.featured_image}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Publishing */}
            <Card className="w-full max-w-full min-w-0 overflow-hidden shadow-sm">
              <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-4">
                <CardTitle className="text-base sm:text-lg">Publishing</CardTitle>
              </CardHeader>
              <CardContent className="w-full max-w-full min-w-0 space-y-4 p-4 pt-0 sm:space-y-5 sm:p-6">
                <div className="grid w-full max-w-full min-w-0 gap-4 sm:gap-6 md:grid-cols-2">
                  <div className="flex w-full max-w-full min-w-0 items-center justify-between rounded-lg border p-3 sm:p-4">
                    <Label htmlFor="is_published" className="cursor-pointer text-sm sm:text-base">
                      Publish now
                    </Label>
                    <Checkbox
                      id="is_published"
                      checked={formData.is_published}
                      onCheckedChange={(c) => setFormData((p) => ({ ...p, is_published: c }))}
                      className="h-4 w-4 shrink-0 sm:h-5 sm:w-5"
                    />
                  </div>
                  <div className="flex w-full max-w-full min-w-0 items-center justify-between rounded-lg border p-3 sm:p-4">
                    <Label htmlFor="is_featured" className="cursor-pointer text-sm sm:text-base">
                      Mark as Featured
                    </Label>
                    <Checkbox
                      id="is_featured"
                      checked={formData.is_featured}
                      onCheckedChange={(c) => setFormData((p) => ({ ...p, is_featured: c }))}
                      className="h-4 w-4 shrink-0 sm:h-5 sm:w-5"
                    />
                  </div>
                </div>

                {errors.submit && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 sm:p-4">
                    <p className="text-xs text-red-600 sm:text-sm">{errors.submit}</p>
                  </div>
                )}

                <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:pt-6">
                  <Link to="/dashboard/blogs" className="w-full sm:flex-1">
                    <Button type="button" variant="outline" size="lg" className="w-full">
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full sm:flex-1"
                    disabled={isSubmitting || !isDirty}>
                    {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : "Update Post"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT COLUMN - LIVE PREVIEW */}
          <LivePreview title="Live Preview" description="Preview how your blog post will look">
            <div className="space-y-4">
              {imagePreview && (
                <div className="aspect-video overflow-hidden rounded-lg">
                  <img src={imagePreview} alt="Featured" className="h-full w-full object-cover" />
                </div>
              )}

              {formData.title && (
                <TextWithTooltip
                  text={formData.title}
                  lineClamp="truncate"
                  className="text-xl font-bold text-gray-900 sm:text-2xl"
                />
              )}

              <div className="flex flex-wrap gap-2">
                {formData.category_id && (
                  <span className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800 sm:px-2.5 sm:text-xs">
                    {categories.find((c) => c.id.toString() === formData.category_id)?.name ||
                      "Category"}
                  </span>
                )}
                {formData.tags
                  .split(",")
                  .filter(Boolean)
                  .map((tag, i) => (
                    <span
                      key={i}
                      className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-800 sm:px-2.5 sm:text-xs">
                      {tag.trim()}
                    </span>
                  ))}
              </div>

              {formData.excerpt && (
                <TextWithTooltip
                  text={formData.excerpt}
                  lineClamp="line-clamp-3"
                  className="border-l-4 border-gray-300 pl-3 text-xs leading-relaxed text-gray-600 italic sm:pl-4 sm:text-sm"
                />
              )}

              {sections.map((section, index) => (
                <div key={section.id} className="space-y-2 border-t pt-3 sm:space-y-3 sm:pt-4">
                  {section.title && (
                    <TextWithTooltip
                      text={section.title}
                      lineClamp="truncate"
                      className="text-lg font-semibold text-gray-900 sm:text-xl"
                    />
                  )}
                  {section.imagePreview && (
                    <div className="overflow-hidden rounded-lg">
                      <img
                        src={section.imagePreview}
                        alt={section.title || `Section ${index + 1}`}
                        className="w-full object-cover"
                      />
                    </div>
                  )}
                  {section.content && <ContentWithTooltip content={section.content} />}
                </div>
              ))}

              {!formData.title &&
                !formData.excerpt &&
                sections.every((s) => !s.title && !s.content) && (
                  <div className="flex flex-col items-center justify-center py-8 text-center sm:py-12">
                    <FileText className="mb-2 h-10 w-10 text-gray-300 sm:mb-3 sm:h-12 sm:w-12" />
                    <p className="text-xs text-gray-500 sm:text-sm">
                      Start filling in the form to see preview
                    </p>
                  </div>
                )}
            </div>
          </LivePreview>
        </div>
      </form>
    </div>
  );
}
